import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

// Component imports
import Sidebar from '../../components/SideBar/Sidebar';
import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import Loading from '../../components/Loading/Loading';
import { ModalDeleteUser } from './Modals/DeleteUser';

// Style and icon imports
import * as Body from './Styles';
import * as defines from '../../../utils/defines/Defines';
import * as Functions from '../../../utils/functions/Functions';
import { State } from '../../redux/store';

// Icons
import Avatar from '../../../assets/images/avatar.png';
import UsuarioIcon from '../../../assets/icons/usuario-icon.svg';
import TecnicoIcon from '../../../assets/icons/tecnico-icon.svg';

// Types
interface UsuarioProps {
  idUsuario: string;
  nomeCompleto: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  email: string;
  tipoTectrol: number;
}

const EditTectrolUser: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const user = state as UsuarioProps;

  // Redux selectors
  const userType = useSelector((state: State) => state.userType.userType);
  const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);

  // Component state
  const [nomeCompleto, setNome] = useState(user.nomeCompleto);
  const [email, setEmail] = useState(user.email);
  const [ddd, setDDD] = useState(user.ddd);
  const [celular, setTelefone] = useState(user.celular);
  const [imagemUsuario, setImagem] = useState(user.fotoUsuario || '');
  const [tipoTectrol, setTipoTectrol] = useState(user.tipoTectrol);
  const [modalDelete, setModalDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);

  // Memoized cargo mapping
  const cargoMap = useMemo(() => ({
    [defines.CargoTectrol['Comercial']]: 'Comercial',
    [defines.CargoTectrol['Engenheiro']]: 'Engenharia',
    [defines.CargoTectrol['Suporte']]: 'Suporte',
    [defines.CargoTectrol['Padrão']]: 'Padrão'
  }), []);

  // Email validation handler
  const handleEmailChange = useCallback((value: string) => {
    const isValidEmail = Functions.verifyEmail(value);
    setEmail(value);
    setErrorEmail(!isValidEmail);
  }, []);

  // Update user details
  const updateUser = useCallback(() => {
    setLoading(true);
    const updatedFields: Partial<UsuarioProps> = {
      ...(email !== user.email && { email }),
      ...(celular !== user.celular && { celular }),
      ...(ddd !== user.ddd && { ddd }),
      ...(nomeCompleto !== user.nomeCompleto && { nomeCompleto })
    };

    if (Object.keys(updatedFields).length === 0) {
      setLoading(false);
      return;
    }

    axios.put(
      `${defines.apiURL}/api/UpdateUser?userTypeRequest=${userType}`,
      {
        ...updatedFields,
        idUsuario: user.idUsuario,
        tipoTectrol: tipoTectrol,
      },
      { withCredentials: true }
    )
    .then(() => {
      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        title: '',
        text: 'Dados do usuario alterados com sucesso',
      });
      setLoading(false);
    })
    .catch((error) => {
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        dispatch,
        error.response.data.content
      );
      setLoading(false);
    });
  }, [
    user, 
    userType, 
    email, 
    celular, 
    ddd, 
    nomeCompleto, 
    tipoTectrol, 
    navigate, 
    dispatch
  ]);

  // Delete user
  const deleteUser = useCallback(() => {
    setLoading(true);
    axios.delete(
      `${defines.apiURL}/api/TectrolCRUD?id=${Functions.ReplaceSpecialCharacters(user.idUsuario)}&userTypeRequest=${userType}`,
      { withCredentials: true }
    )
    .then(() => {
      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        text: 'Usuário excluido com sucesso',
        title: '',
      });
      setModalDelete(false);
      setLoading(false);
      navigate('/tectrol');
    })
    .catch((error) => {
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        dispatch,
        error.response.data.content
      );
      setLoading(false);
    });
  }, [user.idUsuario, userType, navigate, dispatch]);

  // Render role selection card
  const renderRoleCard = (role: number, icon: string, title: string) => (
    <Body.Card
      onClick={() => setTipoTectrol(role)}
      style={{
        border: tipoTectrol === role 
          ? `2px solid ${defines.BlueTec}` 
          : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Body.CardIcon src={icon} />
      <Body.CardTitle>{title}</Body.CardTitle>
    </Body.Card>
  );

  // Check if user has permission to edit
  const hasEditPermission = useMemo(() => 
    acessLevel === defines.CargoTectrol['Suporte'], 
    [acessLevel]
  );

  return (
    <Body.Wrapper>
      <Sidebar
        type={userType}
        content={defines.ContentSidebar.MANAGE_USERS}
        tituloPagina="Editar Administrador"
        backAction={() => navigate('/tectrol')}
      />
      <Body.Container>
        {/* User Photo */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
          }}
        >
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <Body.Photo src={imagemUsuario || Avatar} />
          </div>
        </div>

        {/* User Details Inputs */}
        <Body.Row>
          <Input
            text={nomeCompleto}
            setText={setNome}
            placeholder="Nome Completo"
            title="Nome Completo"
          />
        </Body.Row>
        <Body.Row>
          <Input
            text={email}
            setText={handleEmailChange}
            placeholder="Email"
            title="Email"
            autoComplete='email'
          />
        </Body.Row>
        <Body.Row>
          <Input
            width={'20%'}
            text={ddd}
            setText={setDDD}
            placeholder="DDD"
            title="DDD"
            limit={3}
          />
          <Input
            width={'80%'}
            text={celular}
            setText={setTelefone}
            placeholder="Telefone"
            title="Telefone"
            limit={9}
          />
        </Body.Row>

        {/* Email Error */}
        {errorEmail && (
          <Body.TextError>
            Formato de email inválido ex: email@email.com
          </Body.TextError>
        )}

        {/* Role Selection Rows */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '120px',
          }}
        >
          <Body.Row>
            {renderRoleCard(
              defines.CargoTectrol['Comercial'], 
              UsuarioIcon, 
              'Comercial'
            )}
            {renderRoleCard(
              defines.CargoTectrol['Engenheiro'], 
              TecnicoIcon, 
              'Engenharia'
            )}
          </Body.Row>
        </div>

        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '120px',
          }}
        >
          <Body.Row>
            {renderRoleCard(
              defines.CargoTectrol['Suporte'], 
              TecnicoIcon, 
              'Suporte'
            )}
            {renderRoleCard(
              defines.CargoTectrol['Padrão'], 
              UsuarioIcon, 
              'Padrão'
            )}
          </Body.Row>
        </div>

        {/* Action Buttons */}
        <Body.Row>
          <Button
            text="Salvar"
            color="blue"
            action={updateUser}
            disabled={!hasEditPermission}
          />
          <Button
            text="Excluir Usuário"
            color="red"
            action={() => setModalDelete(true)}
            disabled={!hasEditPermission}
          />
        </Body.Row>

        {/* Loading and Delete Modal */}
        {loading && <Loading />}
        {modalDelete && (
          <ModalDeleteUser
            toggle={setModalDelete}
            idUsuario={user.idUsuario}
            nome={nomeCompleto}
            action={deleteUser}
          />
        )}
      </Body.Container>
    </Body.Wrapper>
  );
};

export default EditTectrolUser;